import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTranslation } from "react-i18next";

interface Candidate {
  id: string;
  name: string;
  skills?: string[];
  match_score?: number;
}

interface CandidateSelectorProps {
  onSelectCandidate: (candidate: Candidate) => void;
  buttonClassName?: string;
}

export const CandidateSelector = ({ onSelectCandidate, buttonClassName }: CandidateSelectorProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch candidates based on search term
  useEffect(() => {
    const fetchCandidates = async () => {
      if (open) {
        setIsLoading(true);
        setError(null);
        
        try {
          let query = supabase
            .from('candidates')
            .select('id, name, skills, match_score');
          
          // Add search filter if there's a search term
          if (searchTerm) {
            query = query.ilike('name', `%${searchTerm}%`);
          }
          
          const { data, error } = await query.limit(10);
          
          if (error) {
            console.error("Error fetching candidates:", error);
            setError("Failed to load candidates");
            setCandidates([]);
            return;
          }
          
          setCandidates(data || []);
        } catch (error) {
          console.error("Failed to fetch candidates:", error);
          setError("An unexpected error occurred");
          setCandidates([]);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCandidates();
  }, [searchTerm, open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("justify-between", buttonClassName)}
        >
          {searchTerm || t("candidates.selector.selectPlaceholder")}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0">
        <Command>
          <CommandInput 
            placeholder={t("candidates.selector.searchPlaceholder")}
            value={searchTerm}
            onValueChange={setSearchTerm}
          />
          {/* Fix: Ensure we render the CommandList with proper content */}
          <CommandList className="max-h-[300px]">
            {isLoading ? (
              <div className="py-6 text-center text-sm text-gray-500">
                {t("candidates.selector.loading")}
              </div>
            ) : error ? (
              <div className="py-6 text-center text-sm text-red-500">
                {t("candidates.selector.error")}
              </div>
            ) : candidates.length === 0 ? (
              <CommandEmpty>{t("candidates.selector.noResults")}</CommandEmpty>
            ) : (
              <CommandGroup>
                {candidates.map((candidate) => (
                  <CommandItem
                    key={candidate.id}
                    value={candidate.id}
                    onSelect={() => {
                      onSelectCandidate(candidate);
                      setSearchTerm(candidate.name);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        searchTerm === candidate.name ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex flex-col">
                      <span>{candidate.name}</span>
                      {candidate.skills && candidate.skills.length > 0 && (
                        <span className="text-xs text-gray-500">
                          {candidate.skills.slice(0, 3).join(", ")}
                          {candidate.skills.length > 3 && "..."}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
